import { getTenantSlug } from '../allauth/lib/django';

export type TenantDeployEnv = 'stage' | 'production';

const APP_DOMAIN = 'strongmindstrongperformance.com';

/**
 * Map org slug to the public host label (smsp → webapp).
 *
 * Slugs are stored with their own casing (TPMG, EBS, TATeers) while hostnames are
 * always lowercase, so fold here — every comparison and host label in this module
 * goes through it.
 */
export function tenantHostSlug(slug: string): string {
  const normalized = (slug || '').toLowerCase();
  return normalized === 'smsp' ? 'webapp' : normalized;
}

/**
 * Absolute home URL for a tenant on stage or production.
 * Appends query params when provided (e.g. appOS, envPicker).
 */
export function buildEnvTenantHomeUrl(
  slug: string,
  env: TenantDeployEnv,
  query: Record<string, string | undefined> = {},
): string {
  const hostSlug = tenantHostSlug(slug);
  const host =
    env === 'stage'
      ? `${hostSlug}.stage.${APP_DOMAIN}`
      : `${hostSlug}.${APP_DOMAIN}`;
  const url = new URL(`https://${host}/`);
  for (const [key, value] of Object.entries(query)) {
    if (value != null && value !== '') {
      url.searchParams.set(key, value);
    }
  }
  return url.toString();
}

/**
 * Build an absolute URL on a sibling tenant subdomain by swapping the leading
 * host label (works across local/stage/prod and preserves the port).
 */
export function buildTenantPathUrl(slug: string, path: string): string {
  const { protocol, host } = window.location;
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  const parts = host.split('.');
  if (parts.length <= 1) {
    return `${protocol}//${host}${normalizedPath}`;
  }
  parts[0] = (slug || '').toLowerCase();
  return `${protocol}//${parts.join('.')}${normalizedPath}`;
}

/** True when `slug` matches the tenant implied by the current hostname. */
export function isCurrentTenantSlug(slug: string | null | undefined): boolean {
  if (!slug) return true;
  return slug.toLowerCase() === getTenantSlug().toLowerCase();
}

/**
 * Same-tenant: return a relative path for in-app navigation.
 * Cross-tenant: return an absolute URL on that org's subdomain.
 */
export function resolveTenantPath(
  orgSlug: string | null | undefined,
  path: string,
): { href: string; external: boolean } {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  if (!orgSlug || isCurrentTenantSlug(orgSlug)) {
    return { href: normalizedPath, external: false };
  }
  return { href: buildTenantPathUrl(orgSlug, normalizedPath), external: true };
}
